import { useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Building2, FolderTree, MoreHorizontal, Pencil, Plus, Settings2, Tag, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { buildOrgServiceInstanceListPath, getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { orgTreeApi, type OrgTreeNode } from '@/services/org-tree'

const withSearch = (pathname: string, params: URLSearchParams, updates: Record<string, string | null | undefined>) => {
  const next = new URLSearchParams(params)
  Object.entries(updates).forEach(([key, value]) => {
    if (value) {
      next.set(key, value)
      return
    }

    next.delete(key)
  })

  const query = next.toString()
  return query ? `${pathname}?${query}` : pathname
}

export default function TreeClientStoresPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const [selectedNode, setSelectedNode] = useState<OrgTreeNode | null>(null)
  const [draftName, setDraftName] = useState('')
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const isNested = location.pathname.includes('/subclient-add/')
  const existingId = searchParams.get('existingId')
  const clientMode = searchParams.get('client') === 'true' || searchParams.get('client') === 'client'

  const nodeQuery = useQuery({
    queryKey: ['org-tree-node', id],
    queryFn: () => orgTreeApi.getNode(id!),
    enabled: Boolean(id),
  })

  const storesQuery = useQuery({
    queryKey: ['org-tree-client-stores', id],
    queryFn: () => orgTreeApi.listChildren(id!, { childNodeType: 1, serviceInstance: true }),
    enabled: Boolean(id),
  })

  const updateMutation = useMutation({
    mutationFn: (payload: { nodeId: string | number; node_name: string }) => orgTreeApi.updateNode(payload.nodeId, { node_name: payload.node_name }),
    onSuccess: async () => {
      toast.success('Store updated successfully')
      setIsEditOpen(false)
      setSelectedNode(null)
      await queryClient.invalidateQueries({ queryKey: ['org-tree-client-stores', id] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (nodeId: string | number) => orgTreeApi.deleteNode(nodeId),
    onSuccess: async () => {
      toast.success('Store deleted successfully')
      setIsDeleteOpen(false)
      setSelectedNode(null)
      await queryClient.invalidateQueries({ queryKey: ['org-tree-client-stores', id] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openEdit = (node: OrgTreeNode) => {
    setSelectedNode(node)
    setDraftName(node.node_name || '')
    setIsEditOpen(true)
  }

  const openDelete = (node: OrgTreeNode) => {
    setSelectedNode(node)
    setIsDeleteOpen(true)
  }

  const backPath = isNested && existingId
    ? `/org/node/client-add/${existingId}`
    : `/org/node/${id}`

  const addStorePath = withSearch('/org/node/client-instrance/form/' + id, searchParams, {
    action: 'add',
    existingId,
    source: isNested ? 'subclient-add' : 'client-add',
  })

  if (nodeQuery.isLoading || storesQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-[28px]" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-44 w-full rounded-[28px]" />
          ))}
        </div>
      </div>
    )
  }

  if (nodeQuery.error || storesQuery.error || !nodeQuery.data) {
    return (
      <div className="rounded-[28px] border border-destructive/20 bg-card p-8 text-center">
        <p className="text-base font-medium text-destructive">Failed to load store nodes</p>
        <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(nodeQuery.error || storesQuery.error)}</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate(withServiceInstanceId(backPath, serviceInstanceId))}>
          Back
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-2">
      <Card className="rounded-[28px] border-border/80">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {isNested ? 'Nested Vendor Stores' : 'Vendor Stores'}
              </h1>
              <p className="max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
                This is the admin-portal version of the legacy Tree vendor-store flow for {nodeQuery.data.node_name || `Node ${nodeQuery.data.id}`}. Use it to open child vendor nodes, service settings, branch nodes, and general details.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" asChild>
                <Link to={withServiceInstanceId(backPath, serviceInstanceId)}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Link>
              </Button>
              {!clientMode ? (
                <Button asChild>
                  <Link to={withServiceInstanceId(addStorePath, serviceInstanceId)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Store
                  </Link>
                </Button>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {(storesQuery.data ?? []).length === 0 ? (
          <Card className="rounded-[28px] border-dashed border-border/70 md:col-span-2 xl:col-span-3">
            <CardContent className="flex min-h-52 flex-col items-center justify-center gap-3 p-8 text-center">
              <Building2 className="h-10 w-10 text-muted-foreground" />
              <div>
                <p className="text-base font-medium text-foreground">No store nodes found</p>
                <p className="mt-1 text-sm text-muted-foreground">Create a store under this vendor node to continue the Tree flow.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          (storesQuery.data ?? []).map((node) => {
            const instanceRef = node.service_instance_data?.[0]?.instance_id || node.service_instance_data?.[0]?.id || null
            const nestedPath = withSearch(`/org/node/subclient-add/${node.id}`, searchParams, {
              existingId: id,
            })
            const settingsPath = withSearch(buildOrgServiceInstanceListPath(node.id), searchParams, {
              client: 'true',
              source: isNested ? 'subclient-add' : 'client-add',
              parentId: id,
            })
            const branchPath = withSearch(`/org/client/child/${node.id}`, searchParams, {
              id: instanceRef ? String(instanceRef) : null,
              name: node.node_name || null,
            })
            const detailsPath = withSearch(`/org/client-details/${node.id}`, searchParams, {
              name: node.node_name || null,
              sub: instanceRef ? String(instanceRef) : null,
              ...(clientMode ? { client: 'client' } : {}),
            })
            const tagsPath = `/services/stores/${node.id}/tags`

            return (
              <Card key={String(node.id)} className="rounded-[28px] border-border/80 transition-shadow hover:shadow-md">
                <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{node.node_name || `Node ${node.id}`}</CardTitle>
                    <CardDescription>ID: {String(node.id)}</CardDescription>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!isNested ? (
                        <DropdownMenuItem asChild>
                          <Link to={withServiceInstanceId(nestedPath, serviceInstanceId)}>
                            <FolderTree className="mr-2 h-4 w-4" />
                            Vendors
                          </Link>
                        </DropdownMenuItem>
                      ) : null}
                      <DropdownMenuItem asChild>
                        <Link to={withServiceInstanceId(settingsPath, serviceInstanceId)}>
                          <Settings2 className="mr-2 h-4 w-4" />
                          Settings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={withServiceInstanceId(branchPath, serviceInstanceId)}>
                          <Building2 className="mr-2 h-4 w-4" />
                          Branches
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={withServiceInstanceId(tagsPath, serviceInstanceId)}>
                          <Tag className="mr-2 h-4 w-4" />
                          Tags
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={withServiceInstanceId(detailsPath, serviceInstanceId)}>
                          <Building2 className="mr-2 h-4 w-4" />
                          General Details
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEdit(node)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => openDelete(node)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-2xl border border-border/70 bg-muted/10 p-4">
                    <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Service Branch</p>
                    <p className="mt-2 break-all text-sm font-medium text-foreground">{String(instanceRef || 'Not linked yet')}</p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" asChild>
                      <Link to={withServiceInstanceId(settingsPath, serviceInstanceId)}>Settings</Link>
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <Link to={withServiceInstanceId(detailsPath, serviceInstanceId)}>Details</Link>
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <Link to={withServiceInstanceId(tagsPath, serviceInstanceId)}>Tags</Link>
                    </Button>
                    {!isNested ? (
                      <Button size="sm" variant="outline" asChild>
                        <Link to={withServiceInstanceId(nestedPath, serviceInstanceId)}>Vendors</Link>
                      </Button>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            )
          })
        )}
      </section>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Store Name</DialogTitle>
            <DialogDescription>Rename the selected store node.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="store-name">Store name</Label>
            <Input id="store-name" value={draftName} onChange={(event) => setDraftName(event.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)} disabled={updateMutation.isPending}>
              Cancel
            </Button>
            <Button
              disabled={updateMutation.isPending || !selectedNode || !draftName.trim()}
              onClick={() => {
                if (selectedNode) {
                  updateMutation.mutate({ nodeId: selectedNode.id, node_name: draftName.trim() })
                }
              }}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Store</DialogTitle>
            <DialogDescription>Remove {selectedNode?.node_name || 'this store'} from the Tree.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending || !selectedNode}
              onClick={() => {
                if (selectedNode) {
                  deleteMutation.mutate(selectedNode.id)
                }
              }}
            >
              Delete Store
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}